import React, { useCallback, useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CrmShell } from "@/components/portal/crm/CrmShell";
import { InstallPrompt } from "@/components/portal/InstallPrompt";
import { ChatWidget } from "@/components/portal/ChatWidget";
import { GuidedTour } from "@/components/portal/GuidedTour";
import { NetworkPing } from "@/components/portal/NetworkPing";
import { FirstFundedCelebration } from "@/components/portal/FirstFundedCelebration";
import { isOpen } from "@/lib/stages";

const TOUR_STEPS = (me) => [
  { route: "/introducer", selector: '[data-testid="referrer-submit-cta"]', title: "Your first case starts here",
    body: "Five short steps — the applicant, their company, the deal, any documents, then a final look before it goes to the desk." },
  { route: "/introducer", selector: '[data-testid="portal-nav-my-cases"]', title: "Everything lives in the rail",
    body: "Cases, the money, your network, your media kit and your paperwork each have their own page. Nothing is buried in a pop-up." },
  { route: "/introducer", selector: '[data-testid="referrer-projected-total"]', title: "What the book is worth to you",
    body: "Every live case, priced off the desks actually holding it. A range, not a quote — and never a promise." },
  ...(me?.agreement?.status !== "signed"
    ? [{ route: "/introducer", selector: '[data-testid="agreement-callout"]', title: "Sign once, then you're live",
         body: "Your introducer agreement takes about twenty seconds. Commission is only payable on signed terms." }]
    : []),
  { route: "/introducer", selector: '[data-testid="referrer-stage-tabs"]', title: "Follow a case all the way",
    body: "Submitted → With lenders → Under assessment → Awaiting documents → Preparing offer → Offer issued → Offer accepted → Funded. These filters follow a case through every stage; open any row for the desks holding it, the paperwork and the thread." },
  { route: "/introducer/account", selector: '[data-testid="apply-links-panel"]', maxHeight: 250, title: "Send a client an application", eyebrow: "Links & invitations",
    body: "Your permanent funding link is already attributed to you — put it anywhere. Or press “Send an application” to invite one client personally by email and text. Every open and every submission is tracked here." },
  { route: "/introducer/network", selector: '[data-testid="referrer-network-link-introducer"]', title: "Grow your own network", eyebrow: "Your network",
    body: "Recruit introducers, lenders and campaign partners with these links. Everyone you bring in earns you an override on their deals — paid by Connexis, never taken from them." },
  { route: "/introducer/network", selector: '[data-testid="referrer-network-recruits"]', maxHeight: 300, title: "Watch their pipeline", eyebrow: "Your network",
    body: "Every recruit's live cases, where they are, and what each is worth to you if it funds. Names stay with them — you see the numbers." },
  { route: "/introducer", title: "Ready to submit your first one?", finishLabel: "Start my first case",
    body: "We'll open the form and coach you through each step. Leave anything optional blank — name, business and amount are the essentials." },
];

const CAMPAIGN_TOUR = [
  { route: "/introducer", selector: '[data-testid="campaign-links"]', title: "Your campaign links", eyebrow: "Campaign partner",
    body: "Everything you send traffic to carries your attribution — the application link, the funding explainer, and a QR for print. Copy them, or download the kit." },
  { route: "/introducer", selector: '[data-testid="campaign-stats"]', title: "What your reach is doing", eyebrow: "Campaign partner",
    body: "Opens, starts, applications, live cases, funded. Updated the moment something moves — never a name, never a phone number." },
  { route: "/introducer", selector: '[data-testid="campaign-cases"]', title: "Every case, masked", eyebrow: "Campaign partner",
    body: "Each application that came through your links, its stage, its size band and what it is worth to you if it funds. Connexis works the case; you earn on the outcome." },
  { route: "/introducer/performance", selector: '[data-testid="referrer-earnings-stats"]', title: "Earnings, honestly", eyebrow: "Campaign partner",
    body: "Owed, paid, and the projected range on live cases. A range, not a quote — and never a promise." },
  { route: "/introducer/channels", selector: '[data-testid="channels-table"]', maxHeight: 300, title: "A link per channel", eyebrow: "Channels",
    body: "One short link per platform, campaign or creative, with the UTMs already on it. Every tap keeps the click id the platform gave us — fbclid, gclid, ttclid — so a funded case traces back to the exact ad." },
  { route: "/introducer/channels", selector: '[data-testid="postback-panel"]', maxHeight: 320, title: "Feed conversions back", eyebrow: "Channels",
    body: "Your ad accounts optimise on conversions. Point a postback at your tracker, or download the platform's own CSV upload file — click ids only, never a person's details.", finishLabel: "Got it" },
];

const AGENT_TOUR = (company) => [
  { route: "/introducer", selector: '[data-testid="referrer-submit-cta"]', title: "Put a case in", eyebrow: "Your seat",
    body: `Five short steps — the applicant, their company, the deal, any documents, then a final look. Every case you submit is attributed to ${company} with your name on it.` },
  { route: "/introducer", selector: '[data-testid="referrer-stage-tabs"]', title: "Follow your cases", eyebrow: "Your seat",
    body: "You see the cases you put in — Submitted → With lenders → Under assessment → Awaiting documents → Preparing offer → Offer issued → Offer accepted → Funded. Open any row for the paperwork and the thread with the Connexis desk." },
  { route: "/introducer", title: "Ready for your first one?", finishLabel: "Start a case", eyebrow: "Your seat",
    body: "Name, business and amount are the essentials. Leave anything optional blank and the desk will ask on the case." },
];

const FLOOR_TOUR = (me) => [
  { route: "/introducer", selector: '[data-testid="floor-stats"]', title: "Your floor at a glance", eyebrow: "Call centre",
    body: "Agents on seats, what came in this month, what is live and what has funded. Every case an agent puts in belongs to your company." },
  { route: "/introducer", selector: '[data-testid="referrer-seat-form"]', maxHeight: 220, title: "Add an agent in seconds", eyebrow: "Your agents",
    body: "A name and a work email is all it takes — they get their own set-up link, their own password, and they only ever see the cases they put in." },
  { route: "/introducer", selector: '[data-testid="referrer-seats-table"]', maxHeight: 320, title: "Set what each agent takes", eyebrow: "Your agents",
    body: "Give any agent a share of your commission on the cases they bring. They see what they have earned; you see what is still to settle. Connexis pays your company — the split stays internal." },
  { route: "/introducer", selector: '[data-testid="referrer-seat-board"]', maxHeight: 300, title: "Who's actually bringing the cases", eyebrow: "Your agents",
    body: "Ranked on funded, then live, then cases in. Set a monthly target on anyone and the bar fills as their cases land." },
  ...(me?.agreement?.status !== "signed"
    ? [{ route: "/introducer", selector: '[data-testid="agreement-callout"]', title: "Sign once, then you're live", eyebrow: "Paperwork",
         body: "Your agreement takes about twenty seconds. Commission is only payable on signed terms." }]
    : []),
  { route: "/introducer/network", selector: '[data-testid="referrer-network-link-introducer"]', title: "Grow beyond the floor", eyebrow: "Your network",
    body: "Recruit other call centres, introducers or media partners and you earn an override on their deals — paid by Connexis, never taken from them." },
  { route: "/introducer", title: "Ready to put the first one in?", finishLabel: "Start a case", eyebrow: "Call centre",
    body: "Name, business and amount are the essentials. Your agents can do the same from their own sign-in." },
];

export default function IntroducerPortal() {
  const { user } = useAuth();
  const nav = useNavigate();
  const { pathname } = useLocation();
  const isAdmin = user?.role === "admin";

  const [me, setMe] = useState(null);
  const [summary, setSummary] = useState(null);
  const [leads, setLeads] = useState([]);
  const [projection, setProjection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unread, setUnread] = useState(0);
  const [tourOpen, setTourOpen] = useState(false);

  const reload = useCallback(async () => {
    try {
      const m = (await api.get("/refer/me")).data;
      setMe(m);
      if (m?.seat) {
        // A seat sees its own cases only — the money endpoints belong to the account holder.
        setLeads((await api.get("/refer/leads")).data.items || []);
      } else {
        const [L, s] = await Promise.all([api.get("/refer/leads"), api.get("/refer/summary")]);
        setLeads(L.data.items || []); setSummary(s.data);
      }
      if (!isAdmin && !m?.tour_completed) setTourOpen(true);
      if (!m?.seat) { try { setProjection((await api.get("/refer/projection")).data); } catch { /* optional */ } }
    } catch { /* the shell still renders */ }
    finally { setLoading(false); }
  }, [isAdmin]);

  useEffect(() => { reload(); }, [reload]);

  // Keeps the live lines current without a page refresh.
  useEffect(() => {
    const t = setInterval(() => {
      if (!document.hidden) api.get("/refer/leads").then((r) => setLeads(r.data.items || [])).catch(() => {});
    }, 60000);
    return () => clearInterval(t);
  }, []);

  const agent = !!me?.seat;

  const meReady = !!me;
  useEffect(() => {
    if (!meReady || agent) return undefined;
    let t;
    const tick = async () => {
      try { setUnread((await api.get("/hub/chat/unread")).data.unread || 0); } catch { /* silent */ }
      t = setTimeout(tick, 25000);
    };
    tick();
    return () => clearTimeout(t);
  }, [agent, meReady]);

  const closeTour = async ({ startCase = false } = {}) => {
    setTourOpen(false);
    if (startCase && !campaign) nav("/introducer/submit");
    if (me && !me.tour_completed) {
      setMe((m) => ({ ...m, tour_completed: true }));
      try { await api.post("/refer/tour-complete"); } catch { /* non-fatal */ }
    }
  };

  const campaign = me?.partner_kind === "campaign";
  const floor = !agent && !campaign && !!me?.floor;   // a call centre — the floor comes first
  const master = floor && !!me?.master;                // invite-only: the floor plus tracked ad links
  const portalName = campaign ? "Campaign portal" : master ? "Master introducer portal" : floor ? "Call centre portal" : "Introducer portal";
  const open = (leads || []).filter((l) => isOpen(l.stage));
  const paperworkDue = me?.agreement?.status === "signed" ? 0 : 1;
  // Only introducers the desk has switched on — never a seat, never a campaign partner.
  const apiItem = me?.api_enabled && !agent && !campaign
    ? [{ to: "/introducer/api", label: "API", icon: "code", testId: "portal-nav-api" }] : [];

  const groups = agent ? [
    { title: "Workspace", items: [
      { to: "/introducer", label: "My cases", icon: "grid", end: true, count: open.length, testId: "portal-nav-my-cases" },
      { to: "/introducer/submit", label: "Submit a case", icon: "plus", testId: "portal-nav-submit" },
      { to: "/introducer/connie", label: "Ask Connie", icon: "sparkle", testId: "portal-nav-connie" },
    ] },
    { title: "Account", items: [
      { to: "/introducer/account", label: "Your seat", icon: "cog", testId: "referrer-nav-documents" },
      { to: "/introducer/feedback", label: "Help shape Connexis", icon: "idea", testId: "portal-nav-feedback" },
    ] },
  ] : campaign ? [
    { title: "Campaign", items: [
      { to: "/introducer", label: "Your campaign", icon: "grid", end: true, count: open.length, testId: "portal-nav-my-cases" },
      { to: "/introducer/channels", label: "Channels & tracking", icon: "link", testId: "portal-nav-channels" },
      { to: "/introducer/media-kit", label: "Media kit & ad rules", icon: "kit", testId: "portal-nav-media-kit" },
      { to: "/introducer/connie", label: "Ask Connie", icon: "sparkle", testId: "portal-nav-connie" },
    ] },
    { title: "Money", items: [
      { to: "/introducer/performance", label: "Earnings", icon: "coins", testId: "portal-nav-performance" },
      { to: "/introducer/statements", label: "Payout statements", icon: "card", testId: "portal-nav-statements" },
    ] },
    { title: "Account", items: [
      { to: "/introducer/messages", label: "Messages", icon: "mail", count: unread, testId: "portal-nav-messages" },
      { to: "/introducer/paperwork", label: "NDA & agreement", icon: "doc", count: paperworkDue, testId: "portal-nav-paperwork" },
      { to: "/introducer/account", label: "Company & setup", icon: "cog", testId: "referrer-nav-documents" },
      { to: "/introducer/feedback", label: "Help shape Connexis", icon: "idea", testId: "portal-nav-feedback" },
    ] },
  ] : floor ? [
    { title: "Your floor", items: [
      { to: "/introducer", label: "The floor", icon: "people", end: true, testId: "portal-nav-floor" },
      { to: "/introducer/cases", label: "All cases", icon: "grid", count: open.length, testId: "portal-nav-my-cases" },
      { to: "/introducer/submit", label: "Submit a case", icon: "plus", testId: "portal-nav-submit" },
      ...(master ? [{ to: "/introducer/channels", label: "Channels & tracking", icon: "link", testId: "portal-nav-channels" }] : []),
      ...apiItem,
      { to: "/introducer/connie", label: "Ask Connie", icon: "sparkle", testId: "portal-nav-connie" },
    ] },
    { title: "Money", items: [
      { to: "/introducer/performance", label: "Earnings", icon: "coins", testId: "portal-nav-performance" },
      { to: "/introducer/statements", label: "Payout statements", icon: "card", testId: "portal-nav-statements" },
    ] },
    { title: "Grow", items: [
      { to: "/introducer/network", label: "Your network", icon: "people", testId: "portal-nav-network" },
      { to: "/introducer/media-kit", label: master ? "Media kit & ad rules" : "Media kit", icon: "kit", testId: "portal-nav-media-kit" },
    ] },
    { title: "Account", items: [
      { to: "/introducer/messages", label: "Messages", icon: "mail", count: unread, testId: "portal-nav-messages" },
      { to: "/introducer/paperwork", label: "NDA & agreement", icon: "doc", count: paperworkDue, testId: "portal-nav-paperwork" },
      { to: "/introducer/account", label: "Company & setup", icon: "cog", testId: "referrer-nav-documents" },
      { to: "/introducer/feedback", label: "Help shape Connexis", icon: "idea", testId: "portal-nav-feedback" },
    ] },
  ] : [
    { title: "Workspace", items: [
      { to: "/introducer", label: "My cases", icon: "grid", end: true, count: open.length, testId: "portal-nav-my-cases" },
      { to: "/introducer/submit", label: "Submit a case", icon: "plus", testId: "portal-nav-submit" },
      ...apiItem,
      { to: "/introducer/connie", label: "Ask Connie", icon: "sparkle", testId: "portal-nav-connie" },
    ] },
    { title: "Money", items: [
      { to: "/introducer/performance", label: "Earnings", icon: "coins", testId: "portal-nav-performance" },
      { to: "/introducer/statements", label: "Payout statements", icon: "card", testId: "portal-nav-statements" },
    ] },
    { title: "Grow", items: [
      { to: "/introducer/network", label: "Your network", icon: "people", testId: "portal-nav-network" },
      { to: "/introducer/media-kit", label: "Media kit", icon: "kit", testId: "portal-nav-media-kit" },
    ] },
    { title: "Account", items: [
      { to: "/introducer/messages", label: "Messages", icon: "mail", count: unread, testId: "portal-nav-messages" },
      { to: "/introducer/paperwork", label: "NDA & agreement", icon: "doc", count: paperworkDue, testId: "portal-nav-paperwork" },
      { to: "/introducer/account", label: "People, access & setup", icon: "cog", testId: "referrer-nav-documents" },
      { to: "/introducer/feedback", label: "Help shape Connexis", icon: "idea", testId: "portal-nav-feedback" },
    ] },
  ];

  const tabs = agent ? [
    { to: "/introducer", label: "Cases", icon: "grid", end: true },
    { to: "/introducer/submit", label: "Submit", icon: "plus" },
    { to: "/introducer/connie", label: "Connie", icon: "sparkle" },
    { to: "/introducer/account", label: "Seat", icon: "cog" },
  ] : campaign ? [
    { to: "/introducer", label: "Campaign", icon: "grid", end: true },
    { to: "/introducer/channels", label: "Channels", icon: "link" },
    { to: "/introducer/connie", label: "Connie", icon: "sparkle" },
    { to: "/introducer/performance", label: "Money", icon: "coins" },
    { to: "/introducer/media-kit", label: "Kit", icon: "kit" },
    { to: "/introducer/messages", label: "Desk", icon: "mail" },
  ] : floor ? [
    { to: "/introducer", label: "Floor", icon: "people", end: true },
    { to: "/introducer/cases", label: "Cases", icon: "grid" },
    { to: "/introducer/submit", label: "Submit", icon: "plus" },
    { to: "/introducer/connie", label: "Connie", icon: "sparkle" },
    master ? { to: "/introducer/channels", label: "Channels", icon: "link" } : { to: "/introducer/performance", label: "Money", icon: "coins" },
    { to: "/introducer/messages", label: "Desk", icon: "mail" },
  ] : [
    { to: "/introducer", label: "Cases", icon: "grid", end: true },
    { to: "/introducer/submit", label: "Submit", icon: "plus" },
    { to: "/introducer/connie", label: "Connie", icon: "sparkle" },
    { to: "/introducer/performance", label: "Money", icon: "coins" },
    { to: "/introducer/media-kit", label: "Kit", icon: "kit" },
    { to: "/introducer/messages", label: "Desk", icon: "mail" },
  ];

  const pending = me?.status === "pending";
  const declined = me?.status === "declined";
  const pills = agent ? [
    { label: "Live partner", tone: "good" },
    { label: `Agent · ${me.seat.name}`, tone: "pale" },
  ] : [
    { label: me?.status === "active" ? "Live partner" : pending ? "Awaiting approval" : declined ? "Not approved" : me?.status || "Invited", tone: me?.status === "active" ? "good" : "pale" },
    { label: campaign ? "Campaign partner" : master ? "Master introducer" : floor ? "Call centre" : me?.agreement?.status === "signed" ? "Company admin" : pending ? "Under review" : "Agreement outstanding", tone: "pale" },
  ];

  if (loading) {
    return <div className="cxp"><div className="empty" style={{ minHeight: "60vh", display: "grid", placeItems: "center" }}>Loading your workspace…</div></div>;
  }

  return (
    <>
      <CrmShell
        kind="introducer"
        railLabel={portalName}
        testId="referrer-portal"
        orgName={me?.company || me?.name || "Your company"}
        orgSub={agent ? "Your seat on the portal" : campaign ? "Campaign partner" : master ? "Master introducer" : floor ? "Call centre partner" : "Introducer partner"}
        pills={pills}
        groups={groups}
        tabs={tabs}
        crumbRoot={portalName}
        crumb={pathname === "/introducer/api" ? "API" : undefined}
        topRight={
          <button className="btn btn-ghost btn-sm" onClick={() => setTourOpen(true)} data-testid="referrer-replay-tour">
            Guide me
          </button>
        }
      >
        {!isAdmin && !campaign && !agent && !pending && <NetworkPing />}
        {pending && <PendingNotice me={me} campaign={campaign} floor={floor} />}
        {declined && <DeclinedNotice />}
        <Outlet context={{ me, setMe, summary, leads, projection, reload, isAdmin, unread, campaign, agent, floor, master, pending }} />
      </CrmShell>

      <GuidedTour steps={agent ? AGENT_TOUR(me?.company || me?.name || "your company")
                              : campaign ? CAMPAIGN_TOUR : floor ? FLOOR_TOUR(me) : TOUR_STEPS(me)}
                  open={tourOpen} onClose={() => closeTour()} onFinish={() => closeTour({ startCase: true })} />
      {agent && me?.celebration && !tourOpen && (
        <FirstFundedCelebration celebration={me.celebration} me={me}
                                onDone={() => setMe((m) => (m ? { ...m, celebration: null } : m))} />
      )}
      {meReady && !agent && <ChatWidget portalLabel={portalName} />}
      <InstallPrompt />
    </>
  );
}

/* A partner who signed themselves up can sign in straight away; cases wait on the desk. Seats do not —
   a call centre can have its floor set up and ready before the first case is allowed through. */
const PendingNotice = ({ me, campaign, floor }) => (
  <div className="warn" data-testid="pending-approval-banner" style={{ marginBottom: 22 }}>
    <span>
      <b>Your account is under review.</b> A person on the desk checks every new partner before the first case —
      usually within one working day, and you will get an email the moment it is done.{" "}
      {campaign
        ? "Your tracked links and media kit go live on approval; your agreement arrives to e-sign at the same time."
        : floor
          ? "You can add your agents and send their set-up links now — cases open on approval, and your agreement arrives to e-sign at the same time."
          : "You can set your company up now; cases open on approval, and your agreement arrives to e-sign at the same time."}{" "}
      Signed up as <b>{me?.company || me?.name}</b>{me?.referred_by_name ? <> · introduced by <b>{me.referred_by_name}</b></> : null}.
    </span>
  </div>
);

const DeclinedNotice = () => (
  <div className="warn" data-testid="declined-banner" style={{ marginBottom: 22 }}>
    <span>
      <b>We were not able to approve this account.</b> You should have an email from the desk explaining why. If you
      would like to talk it through, write to <a href="mailto:ops@connex.is" style={{ fontWeight: 800, textDecoration: "underline" }}>ops@connex.is</a>.
    </span>
  </div>
);

export const AdminPreviewBanner = ({ what = "introducer portal" }) => (
  <div className="warn" data-testid="admin-preview-banner" style={{ marginBottom: 20 }}>
    <span>
      <b>Admin preview.</b> You&apos;re viewing the {what} as Connexis admin — partner actions need a partner
      account. Run the desk from the <a href="/hub" style={{ fontWeight: 800, textDecoration: "underline" }}>Hub</a> instead.
    </span>
  </div>
);
